/*
 * Save Your Cart (retail) — replaces the retired CartSave app.
 * Markup: snippets/clnf-save-cart.liquid, styles: assets/clnf-save-cart.css
 *
 * 1. Save:    email + consent -> Klaviyo list subscription, the cart written to the profile
 *             (saved_cart_* properties), then a "Cart Saved" event that starts the flow.
 * 2. Sync:    after opting in, every add / change / remove in the same browser session re-writes
 *             the saved_cart_* profile properties, so the emails show the cart as it is now.
 *             Boot (snippets/clnf-save-cart.liquid) loads this file in sync-only mode
 *             (window.__clnfScSync) on pages where the popup itself isn't needed.
 * 3. Restore: /cart?clnf_cart=<variantId>:<qty>,... rebuilds the cart without doubling
 *             quantities for items that are already in it.
 * 4. Open:    window.clnfSaveCart.open() / window.clnfSaveCartOpen() (defined in the snippet, works
 *             before this file has loaded) open the popup on demand, e.g. from a button in the
 *             UpNova cart drawer. window.__clnfScOpen asks this file to open it as soon as it loads.
 *
 * The Klaviyo client endpoints take the PUBLIC company id only (never a private key).
 */
(function () {
  'use strict';

  // 2024-10-15 (used by templates/customers/addresses.liquid) rejects `subscriptions` inside the
  // profile with a 400, which silently broke the first version of this form. 2026-07-15 is current.
  var KLAVIYO_REVISION = '2026-07-15';
  var KLAVIYO_COMPANY_FALLBACK = 'Tx3zFn';
  var METRIC_NAME = 'Cart Saved';
  var RESTORE_PARAM = 'clnf_cart';
  var SAVED_KEY = 'clnf_sc_saved';
  var RESTORED_KEY = 'clnf_sc_restored';
  var PROMPTED_KEY = 'clnf_sc_prompted'; // shared with the boot script in snippets/clnf-save-cart.liquid
  var EMAIL_KEY = 'clnf_sc_email'; // sessionStorage only: dies with the tab, never persisted
  var EXIT_COUNT_KEY = 'clnf_sc_exit_n';
  var LAST_SHOWN_KEY = 'clnf_sc_last_shown';
  var EXIT_MAX_PER_SESSION = 3;
  var EXIT_COOLDOWN_MS = 3 * 60 * 1000;
  var CART_CHANGE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var MAX_LINES = 40;
  var syncTimer = null;

  // Sync-only mode: the shopper already saved their cart earlier in this session, so no dialog is
  // needed here. Everything below is hoisted function declarations, safe to call from this point.
  if (window.__clnfScSync && !window.__clnfScOpen) {
    if (ssGet(EMAIL_KEY)) {
      watchCartChanges();
      scheduleSync();
    }
    return;
  }
  // The full popup can be loaded after sync mode already ran (drawer button on a later click):
  // never run the popup twice on one page.
  if (window.clnfSaveCart) {
    if (window.__clnfScOpen) { var from = window.__clnfScOpen; window.__clnfScOpen = false; window.clnfSaveCart.open(from); }
    return;
  }

  var root = document.getElementById('clnf-save-cart');
  if (!root) {
    // Non-cart pages ship the dialog inside an inert <template>; clone it on first use.
    var tpl = document.getElementById('clnf-sc-tpl');
    if (tpl && tpl.content) {
      document.body.appendChild(tpl.content.cloneNode(true));
      root = document.getElementById('clnf-save-cart');
    }
  }
  if (!root) return;
  var listId = root.getAttribute('data-list-id');

  function store(kind) {
    try { return window[kind]; } catch (e) { return null; }
  }
  function ssGet(k) { try { return store('sessionStorage').getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { store('sessionStorage').setItem(k, v); } catch (e) {} }

  /* ---------------------------------------------------------------- Klaviyo */

  function getCompanyId() {
    var scripts = document.getElementsByTagName('script');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].src || '';
      var match = src.match(/[?&]company_id=([^&]+)/);
      if (match && /klaviyo\.js/i.test(src)) return decodeURIComponent(match[1]);
    }
    return KLAVIYO_COMPANY_FALLBACK;
  }

  function klaviyoPost(path, body) {
    return fetch('https://a.klaviyo.com' + path + '?company_id=' + encodeURIComponent(getCompanyId()), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('Klaviyo ' + path + ' ' + res.status);
      return res;
    });
  }

  function subscribe(email) {
    return klaviyoPost('/client/subscriptions/', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Save your cart - ' + openedFrom,
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    });
  }

  // One shape for both the event and the profile properties; the Klaviyo email templates loop over
  // saved_cart_items as product.name / product.images.0.url / product.price / quantity.
  function buildCart(cart) {
    var lines = [];
    var permalink = [];
    var items = cart.items || [];
    for (var i = 0; i < items.length && i < MAX_LINES; i++) {
      var it = items[i];
      permalink.push(it.variant_id + ':' + it.quantity);
      lines.push({
        product: {
          name: it.product_title + (it.variant_title ? ' - ' + it.variant_title : ''),
          images: [{ url: it.image || '' }],
          price: (it.final_line_price || 0) / 100
        },
        quantity: it.quantity
      });
    }
    return {
      lines: lines,
      count: cart.item_count || 0,
      total: (cart.total_price || 0) / 100,
      currency: cart.currency || 'USD',
      url: permalink.length
        ? window.location.origin + '/cart?' + RESTORE_PARAM + '=' + permalink.join(',') +
          '&utm_source=klaviyo&utm_medium=email&utm_campaign=save_your_cart'
        : window.location.origin + '/cart'
    };
  }

  // The emails read the cart from the profile so that they reflect what is in it *now*, not what
  // was in it when the popup was first submitted. saved_cart_count lets the flow skip emptied carts.
  function saveCartToProfile(email, cart) {
    var c = buildCart(cart);
    return klaviyoPost('/client/profiles/', {
      data: {
        type: 'profile',
        attributes: {
          email: email,
          properties: {
            saved_cart_url: c.url,
            saved_cart_items: c.lines,
            saved_cart_count: c.count,
            saved_cart_total: c.total,
            saved_cart_updated: new Date().toISOString()
          }
        }
      }
    });
  }

  function trackCartSaved(email, cart) {
    var c = buildCart(cart);
    return klaviyoPost('/client/events/', {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: METRIC_NAME } } },
          profile: { data: { type: 'profile', attributes: { email: email } } },
          properties: {
            source: 'clnf-save-cart-popup',
            opened_from: openedFrom,
            reclaimURL: c.url,
            item_count: c.count,
            cart_total: c.total,
            $extra: { line_items: c.lines }
          },
          value: c.total,
          value_currency: c.currency,
          unique_id: 'save-' + email + '-' + Date.now()
        }
      }
    });
  }

  /* ------------------------------------------------------------- Cart sync */

  function syncCart() {
    var email = ssGet(EMAIL_KEY);
    if (!email) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return saveCartToProfile(email, cart); })
      .catch(function () {});
  }

  // Debounced: a burst of changes (qty spinners, bulk adds) becomes a single profile write.
  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncCart, 2500);
  }

  // PerformanceObserver sees fetch/XHR without wrapping window.fetch / XMLHttpRequest.
  function watchCartChanges() {
    if (window.__clnfScWatching || !('PerformanceObserver' in window)) return;
    window.__clnfScWatching = true;
    try {
      var po = new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          if (CART_CHANGE_RE.test(entries[i].name) && !(entries[i].responseStatus >= 400)) {
            scheduleSync();
            return;
          }
        }
      });
      po.observe({ type: 'resource' });
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ Modal */

  var dialog = root.querySelector('.clnf-sc__dialog');
  var emailInput = document.getElementById('clnf-sc-email');
  var honeypot = document.getElementById('clnf-sc-website');
  var errorEl = document.getElementById('clnf-sc-error');
  var submitBtn = document.getElementById('clnf-sc-submit');
  var lastFocus = null;
  var openedAt = 0;
  // Which trigger opened the popup, sent to Klaviyo so each one's sign-ups can be compared.
  var openedFrom = 'unknown';

  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.hidden = !msg;
  }

  function showPanel(name) {
    var panels = root.querySelectorAll('[data-clnf-sc-panel]');
    for (var i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-clnf-sc-panel') !== name;
    }
  }

  function openModal(from) {
    if (!root.hidden) return;
    openedFrom = typeof from === 'string' && from ? from : 'button';
    // UpNova opens its drawer + backdrop at the max z-index right after add-to-cart; being last in
    // <body> is what puts the popup above it (on the cart page the dialog renders mid-page).
    if (root.parentNode !== document.body || root.nextElementSibling) document.body.appendChild(root);
    ssSet(PROMPTED_KEY, '1'); // opened by hand or automatically: either way don't auto-prompt again
    ssSet(LAST_SHOWN_KEY, String(Date.now())); // exit intent waits EXIT_COOLDOWN_MS after any showing
    lastFocus = document.activeElement;
    showError('');
    showPanel('ask');
    root.hidden = false;
    openedAt = Date.now();
    document.addEventListener('keydown', onKeydown, true);
    window.setTimeout(function () { (emailInput || dialog).focus(); }, 0);
  }

  function closeModal() {
    root.hidden = true;
    document.removeEventListener('keydown', onKeydown, true);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { closeModal(); return; }
    if (e.key !== 'Tab') return;
    var f = dialog.querySelectorAll('button, input:not([tabindex="-1"]), [href]');
    var visible = [];
    for (var i = 0; i < f.length; i++) {
      if (f[i].offsetParent !== null) visible.push(f[i]);
    }
    if (!visible.length) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function submit() {
    var email = (emailInput.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      showError('Please enter a valid email address.');
      emailInput.focus();
      return;
    }
    showError('');
    submitBtn.disabled = true;

    var sentTo = document.getElementById('clnf-sc-sent-to');
    function done(real) {
      ssSet(SAVED_KEY, '1');
      if (real === true) {
        // Keep the saved cart current while they carry on shopping in this session.
        ssSet(EMAIL_KEY, email);
        watchCartChanges();
      }
      if (sentTo) sentTo.textContent = email;
      showPanel('done');
      submitBtn.disabled = false;
    }

    // Bots fill the honeypot or submit instantly: pretend success, send nothing.
    if ((honeypot && honeypot.value) || Date.now() - openedAt < 1500) { done(false); return; }

    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      try { window.klaviyo.identify({ email: email }); } catch (e) {}
    }

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) throw new Error('empty');
        // Order matters: consent first (profile joins the list), then the cart on the profile
        // (what the emails read), then the event that starts the flow.
        return subscribe(email)
          .then(function () { return saveCartToProfile(email, cart); })
          .then(function () { return trackCartSaved(email, cart); });
      })
      .then(function () { done(true); })
      .catch(function (err) {
        submitBtn.disabled = false;
        showError(err && err.message === 'empty'
          ? "Your cart is empty. Add something to it and we'll hold it for you."
          : "Sorry, we couldn't save your cart just now. Please try again in a moment.");
      });
  }

  /* ---------------------------------------------------------------- Restore */

  function showRestoreOverlay() {
    var el = document.createElement('div');
    el.className = 'clnf-sc__restore';
    el.setAttribute('role', 'status');
    el.innerHTML = '<div class="clnf-sc__spinner" aria-hidden="true"></div><h2>Loading your cart&hellip;</h2>';
    document.body.appendChild(el);
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function parseRestore(raw) {
    if (!/^\d+:\d{1,3}(,\d+:\d{1,3})*$/.test(raw)) return null;
    var parts = raw.split(',').slice(0, MAX_LINES);
    return parts.map(function (p) {
      var kv = p.split(':');
      return { id: Number(kv[0]), quantity: Number(kv[1]) };
    });
  }

  function cleanUrl() {
    var url = new URL(window.location.href);
    url.searchParams.delete(RESTORE_PARAM);
    return url.pathname + (url.search || '');
  }

  function restoreCart(raw) {
    var wanted = parseRestore(raw);
    if (!wanted) { window.location.replace('/cart'); return; }
    // Already restored this exact link in this tab: just drop the param, never loop.
    if (ssGet(RESTORED_KEY) === raw) { window.location.replace(cleanUrl()); return; }
    ssSet(RESTORED_KEY, raw);
    // They came back from their saved-cart email: never ask them to save it again this session.
    ssSet(SAVED_KEY, '1');
    showRestoreOverlay();

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var have = {};
        (cart.items || []).forEach(function (it) { have[it.variant_id] = it.quantity; });
        var toAdd = [];
        var toRaise = [];
        wanted.forEach(function (w) {
          if (have[w.id] === undefined) toAdd.push(w);
          else if (have[w.id] < w.quantity) toRaise.push(w); // never lower, never double
        });

        // Raise sequentially (cart/change.js is one line at a time).
        var chain = toRaise.reduce(function (p, w) {
          return p.then(function () { return postJson('/cart/change.js', { id: w.id, quantity: w.quantity }); });
        }, Promise.resolve());

        return chain.then(function () {
          if (!toAdd.length) return null;
          return postJson('/cart/add.js', { items: toAdd }).then(function (res) {
            if (res.ok) return null;
            // One unavailable variant fails the whole batch: retry line by line, skip failures.
            return toAdd.reduce(function (p, w) {
              return p.then(function () { return postJson('/cart/add.js', { items: [w] }).catch(function () {}); });
            }, Promise.resolve());
          });
        });
      })
      .catch(function () {})
      .then(function () { window.location.replace(cleanUrl()); });
  }

  /* ------------------------------------------------------------ Exit intent */

  // Unlike the add-to-cart prompt (once per session), exit intent may ask again on later pages:
  // asking once per session left most people who leave never asked. Limits: EXIT_MAX_PER_SESSION
  // times, EXIT_COOLDOWN_MS after the popup last showed, once per page, never once saved.
  var exitFiredThisPage = false;

  function exitAllowed() {
    if (exitFiredThisPage || !root.hidden || ssGet(SAVED_KEY)) return false;
    if (Number(ssGet(EXIT_COUNT_KEY) || 0) >= EXIT_MAX_PER_SESSION) return false;
    return Date.now() - Number(ssGet(LAST_SHOWN_KEY) || 0) >= EXIT_COOLDOWN_MS;
  }

  function exitIntent(from) {
    if (!exitAllowed()) return;
    exitFiredThisPage = true; // claimed before the cart check so two triggers can't both open it
    // The cart can change without a page load, so ask the cart rather than trusting data-has-items.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.item_count || !root.hidden || ssGet(SAVED_KEY)) { exitFiredThisPage = false; return; }
        root.setAttribute('data-has-items', 'true');
        ssSet(EXIT_COUNT_KEY, String(Number(ssGet(EXIT_COUNT_KEY) || 0) + 1));
        openModal(from);
      })
      .catch(function () { exitFiredThisPage = false; });
  }

  function armExitIntent() {
    var armedAt = Date.now() + 8000; // ignore the first 8s on a page
    var mouse = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (mouse) {
      // Desktop: the pointer leaves through the top of the window (tabs, address bar, close).
      document.addEventListener('mouseout', function (e) {
        if (e.relatedTarget || e.clientY > 0 || Date.now() < armedAt) return;
        exitIntent('exit intent');
      });
      return;
    }
    // Touch has no pointer to watch. Two signals that someone is about to leave:
    // 1. a fast flick back up toward the address bar after reading well down the page;
    var lastY = window.scrollY;
    var lastT = Date.now();
    var maxY = lastY;
    window.addEventListener('scroll', function () {
      var y = window.scrollY;
      var t = Date.now();
      if (y > maxY) maxY = y;
      var dt = t - lastT;
      if (t >= armedAt && dt > 0 && dt < 250 && maxY > 600 && maxY - y > 400 && (lastY - y) / dt > 1.5) {
        exitIntent('exit intent mobile');
      }
      lastY = y;
      lastT = t;
    }, { passive: true });
    // 2. coming back after 15s+ in another app or tab (comparing prices, got distracted).
    var hiddenAt = 0;
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt >= 15000 && Date.now() >= armedAt) exitIntent('return to tab');
      hiddenAt = 0;
    });
  }

  // When this file was loaded for exit intent (the cart already had items), the boot script has
  // stopped watching, so the first add-to-cart of the session still gets its prompt from here.
  function watchAdds() {
    if (!('PerformanceObserver' in window)) return;
    try {
      var po = new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          if (CART_ADD_RE.test(entries[i].name) && !(entries[i].responseStatus >= 400)) {
            if (!ssGet(PROMPTED_KEY) && !ssGet(SAVED_KEY)) promptAfterAdd();
            return;
          }
        }
      });
      po.observe({ type: 'resource' });
    } catch (e) {}
  }

  /* ----------------------------------------------- First add-to-cart prompt */

  // The boot script (every page) loads this file right after a successful /cart/add and sets
  // window.__clnfScPrompt. This was CartSave's main capture point: ask once per session, right
  // when someone commits to a product.
  //
  // Timing: UpNova opens its cart drawer on add-to-cart. Asking while it is open means two layers
  // fighting for attention, so wait until the shopper closes the drawer, then ask. If they go
  // straight to checkout from the drawer they are never asked, which is the point.

  // UpNova renders the drawer panel inside #upezCart's shadow root (display:none while closed).
  // The scroll lock on <body> is the fallback signal if UpNova's markup changes, and also covers
  // any other drawer/modal that is open at that moment.
  function cartDrawerOpen() {
    var host = document.getElementById('upezCart');
    var panel = host && host.shadowRoot && host.shadowRoot.querySelector('div.fixed.right-0.top-0');
    if (panel) {
      var r = panel.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return true;
    }
    return window.getComputedStyle(document.body).overflow === 'hidden';
  }

  function promptAfterAdd() {
    if (ssGet(SAVED_KEY) || ssGet(PROMPTED_KEY)) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.item_count) return; // the add failed (e.g. sold out): don't prompt
        root.setAttribute('data-has-items', 'true');
        var start = Date.now();
        var sawDrawer = false;
        // Polls only while waiting (max 10 min), at a rate that costs nothing.
        (function check() {
          if (!root.hidden || ssGet(SAVED_KEY) || ssGet(PROMPTED_KEY)) return;
          var waited = Date.now() - start;
          if (cartDrawerOpen()) {
            sawDrawer = true;
            if (waited < 600000) window.setTimeout(check, 300);
            return;
          }
          // No drawer yet: give it 1.5s to open (it animates in after the add lands).
          if (!sawDrawer && waited < 1500) { window.setTimeout(check, 300); return; }
          // Drawer just closed (or there is none): a short beat so it doesn't feel like a reaction to the close.
          window.setTimeout(function () {
            if (!root.hidden || cartDrawerOpen() || ssGet(SAVED_KEY) || ssGet(PROMPTED_KEY)) {
              if (root.hidden && cartDrawerOpen()) check(); // reopened in that beat: wait again
              return;
            }
            openModal('add to cart');
          }, sawDrawer ? 600 : 0);
        })();
      })
      .catch(function () {});
  }

  /* ------------------------------------------------------------------- Init */

  var restoreRaw = new URLSearchParams(window.location.search).get(RESTORE_PARAM);
  if (restoreRaw) { restoreCart(restoreRaw); return; }

  window.clnfSaveCart = { open: openModal, close: closeModal };

  // composedPath() so a [data-clnf-sc-open] button inside a shadow root (app cart drawers) still counts.
  function matchIn(e, sel) {
    var path = e.composedPath ? e.composedPath() : [e.target];
    for (var i = 0; i < path.length; i++) {
      if (path[i].matches && path[i].matches(sel)) return path[i];
    }
    return null;
  }
  document.addEventListener('click', function (e) {
    var opener = matchIn(e, '[data-clnf-sc-open]');
    if (opener) { e.preventDefault(); openModal(opener.getAttribute('data-clnf-sc-open')); }
    else if (matchIn(e, '[data-clnf-sc-close]')) { e.preventDefault(); closeModal(); }
  });
  if (submitBtn) submitBtn.addEventListener('click', submit);
  if (emailInput) {
    emailInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
    });
  }
  armExitIntent();
  watchAdds();
  if (window.__clnfScOpen) { var from = window.__clnfScOpen; window.__clnfScOpen = false; openModal(from); }
  else if (window.__clnfScPrompt) promptAfterAdd();
  // Cart page (or any page that loaded this file) after the cart was already saved this session.
  if (ssGet(SAVED_KEY) && ssGet(EMAIL_KEY)) watchCartChanges();
})();
